import { LoadingOverlay } from '@mantine/core'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isNil } from '@semios/app-platform-common'
import type { TLngLat } from 'App/Map/types'
import { ConfirmationSettingsModal } from 'components/ConfirmationSettingsModal/ConfirmationSettingsModal'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback, useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TPoint } from 'stores/fieldAssetStore/helperTypes'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { ValueGroupDictionary } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { apiFetch } from 'utils/apiFetch'
import { removeCmOrInFromSoilProbeDepths } from 'utils/removeCmOrInFromSoilProbeDepths'
import { showNotification } from 'utils/showNotification'
import { refreshMapData } from '../../App/Map/PanelDetails/_utils/refreshMapData'
import { SoilCalibrationModalContents } from './SoilCalibrationModalContents/SoilCalibrationModalContents'
import { useSoilCalibrationData } from './_utils/useSoilCalibrationData'

export const SoilCalibrationModal = () => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const soilCalibrationSettingsOpened = detailsPanelStore.useSelector((s) => s.soilCalibrationSettingsOpened)
  const selectedPoint = selectedFieldAssetsStore.useSelector((s) => s.soilPoint)
  const [confirmModalOpened, setConfirmModalOpened] = useState(false)
  const [refreshChartAndSummary, setRefreshChartAndSummary] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [pwp, setPwp] = useState<number | ''>('')
  const [fc, setFc] = useState<number | ''>('')
  const [selectedStation, setSelectedStation] = useState<TLngLat | null>(null)
  const [stagedStation, setStagedStation] = useState<TLngLat | null>(null)
  const [selectedDepth, setSelectedDepth] = useState<number | null>(null)
  const [stagedDepth, setStagedDepth] = useState<number | null>(null)
  const [stagedClose, setStagedClose] = useState(false)

  const defaultDates: [Date | null, Date | null] = [
    moment.tz(moment.tz.guess()).subtract(3, 'months').toDate(),
    moment.tz(moment.tz.guess()).toDate(),
  ]

  const [dates, setDates] = useState<[Date | null, Date | null]>(defaultDates)

  const [lastGoodReading, setLastGoodReading] =
    useState<routes.SoilCalibrationSettingsGet.Response['lastGoodReading']>('')

  const [defaultLastGoodReading, setDefaultLastGoodReading] =
    useState<routes.SoilCalibrationSettingsGet.Response['lastGoodReading']>('')

  const [overrides, setOverrides] = useState<routes.SoilCalibrationSettingsGet.Response['overrides']>({})
  const [data, setData] = useState<NonNullable<routes.Values.Response['points']>>({})

  const allPropertiesPoints = Object.values(properties ?? {}).reduce<Record<string, TPoint>>(
    (acc, property) => {
      if (property.points) {
        Object.entries(property.points).forEach(([lngLat, point]) => {
          acc[lngLat] = point
        })
      }

      return acc
    },
    {},
  )

  const propertyStation = selectedStation && allPropertiesPoints[selectedStation]
  const soilProbeDepthsAvailable = propertyStation?.configuration?.soilProbeDepthsAvailable ?? []

  const getOverrideValues = useCallback(
    (depth: number, overrideData = overrides) => {
      const overrideValues = overrideData[depth]

      return {
        fc: overrideValues?.fieldCapacity ?? '',
        pwp: overrideValues?.permanentWiltingPoint ?? '',
      }
    },
    [overrides],
  )

  const setupDepthAndOverrides = useCallback(
    (depth: number, freshOverrides: routes.SoilCalibrationSettingsGet.Response['overrides']) => {
      setSelectedDepth(depth)

      const { fc: overrideFc, pwp: overridePwp } = getOverrideValues(depth, freshOverrides)

      setFc(overrideFc)

      setPwp(overridePwp)
    },
    [getOverrideValues],
  )

  const currentOverrideValues = selectedDepth ? getOverrideValues(selectedDepth) : { fc: '', pwp: '' }

  const somethingHasChanged =
    fc !== currentOverrideValues.fc ||
    pwp !== currentOverrideValues.pwp ||
    lastGoodReading !== defaultLastGoodReading

  const { fetchSoilData } = useSoilCalibrationData(
    dates,
    data,
    setData,
    setOverrides,
    setDefaultLastGoodReading,
    setLastGoodReading,
    setLoading,
  )

  const handleStationChange = useCallback(
    async (newStation: TLngLat) => {
      if (somethingHasChanged) {
        setStagedStation(newStation)

        setConfirmModalOpened(true)

        return
      }

      setSelectedStation(newStation)

      const stationPoints = allPropertiesPoints[newStation]
      const depthsAvailable = stationPoints?.configuration?.soilProbeDepthsAvailable ?? []

      if (depthsAvailable.length > 0) {
        const firstDepth = removeCmOrInFromSoilProbeDepths(depthsAvailable[0])

        await fetchSoilData(newStation, undefined, (freshOverrides) => {
          setupDepthAndOverrides(firstDepth, freshOverrides)
        })
      } else {
        await fetchSoilData(newStation)
      }
    },
    [
      somethingHasChanged,
      setStagedStation,
      setConfirmModalOpened,
      setSelectedStation,
      allPropertiesPoints,
      fetchSoilData,
      setupDepthAndOverrides,
    ],
  )

  const handleDepthChange = useCallback(
    (newDepth: string | null) => {
      const depthNumber = newDepth ? Number(newDepth) : null

      if (somethingHasChanged) {
        setStagedDepth(depthNumber)

        setConfirmModalOpened(true)

        return
      }

      setSelectedDepth(depthNumber)

      if (depthNumber) {
        const { fc: overrideFc, pwp: overridePwp } = getOverrideValues(depthNumber)

        setFc(overrideFc)

        setPwp(overridePwp)
      }
    },
    [
      somethingHasChanged,
      setStagedDepth,
      setConfirmModalOpened,
      setSelectedDepth,
      getOverrideValues,
      setFc,
      setPwp,
    ],
  )

  const handleDateChange = useCallback(
    async (newDates: [Date | null, Date | null]) => {
      setDates(newDates)

      if (selectedStation && newDates[0] && newDates[1]) {
        await fetchSoilData(selectedStation, newDates)
      }
    },
    [selectedStation, fetchSoilData, setDates],
  )

  const handleResetAndClose = useCallback(() => {
    if (stagedDepth) {
      setSelectedDepth(stagedDepth)

      const { fc: overrideFc, pwp: overridePwp } = getOverrideValues(stagedDepth)

      setFc(overrideFc)

      setPwp(overridePwp)

      setStagedDepth(null)
    } else if (selectedDepth) {
      const { fc: overrideFc, pwp: overridePwp } = getOverrideValues(selectedDepth)

      setFc(overrideFc)

      setPwp(overridePwp)
    }

    setLastGoodReading(defaultLastGoodReading)

    if (stagedStation) {
      setSelectedStation(stagedStation)

      setStagedStation(null)

      fetchSoilData(stagedStation)
    }

    if (stagedClose) {
      setStagedClose(false)

      detailsPanelStore.setState((prev) => ({
        ...prev,
        soilCalibrationSettingsOpened: false,
      }))
    }
  }, [
    stagedDepth,
    stagedStation,
    selectedDepth,
    defaultLastGoodReading,
    fetchSoilData,
    getOverrideValues,
    setLastGoodReading,
    setSelectedDepth,
    setFc,
    setPwp,
    setStagedDepth,
    setSelectedStation,
    setStagedStation,
    stagedClose,
  ])

  const handleUpdate = useCallback(async () => {
    if (!selectedStation || !selectedDepth) return

    try {
      setIsUpdating(true)

      const apiCalls = []

      if (lastGoodReading !== defaultLastGoodReading) {
        const url =
          lastGoodReading === ''
            ? '/soil-calibration-good-readings-delete'
            : '/soil-calibration-good-readings-set'

        const body =
          lastGoodReading === ''
            ? { lngLat: selectedStation }
            : { lngLat: selectedStation, lastGoodReading: new Date(lastGoodReading).toISOString() }

        apiCalls.push(
          apiFetch({ url, body }).then((response) => {
            if (response === true) {
              showNotification({
                type: 'success',
                message: translate.phrases.banyanApp('Successfully updated {{label}}', {
                  label: translate.phrases.banyanApp('Last Good Reading'),
                }),
              })

              setDefaultLastGoodReading(lastGoodReading)
            } else {
              showNotification({
                type: 'error',
                message: translate.phrases.banyanApp('There was an error'),
              })
            }
          }),
        )
      }

      const { fc: originalFc, pwp: originalPwp } = getOverrideValues(selectedDepth)
      const fcHasChanged = fc !== originalFc
      const pwpHasChanged = pwp !== originalPwp

      if (fcHasChanged || pwpHasChanged) {
        if (isNil(pwp) || pwp === '') {
          showNotification({
            type: 'error',
            message: translate.phrases.validation('{{label}} is required', {
              label: translate.phrases.abbreviations('Permanent Wilting Point'),
            }),
          })

          setIsUpdating(false)

          return
        }

        if (Number(pwp) >= Number(fc)) {
          showNotification({
            type: 'error',
            message: translate.phrases.validation('{{label}} must be > {{value}}', {
              label: translate.phrases.abbreviations('Field Capacity'),
              value: translate.phrases.abbreviations('Permanent Wilting Point'),
            }),
          })

          setIsUpdating(false)

          return
        }

        if (fc !== '') {
          setOverrides((prev) => ({
            ...prev,
            [selectedDepth]: {
              fieldCapacity: fc,
              permanentWiltingPoint: pwp,
            },
          }))

          apiCalls.push(
            apiFetch({
              url: '/soil-calibration-settings-set',
              body: {
                lngLat: selectedStation,
                depthCM: selectedDepth,
                pwp,
                fc,
              },
            }).then((response) => {
              if (response === true) {
                showNotification({
                  type: 'success',
                  message: translate.phrases.banyanApp('Successfully updated {{label}}', {
                    label: translate.phrases.banyanApp('FC/PWP overrides'),
                  }),
                })
              } else {
                showNotification({
                  type: 'error',
                  message: translate.phrases.banyanApp('There was an error updating {{label}}', {
                    label: translate.phrases.banyanApp('FC/PWP overrides'),
                  }),
                })
              }
            }),
          )
        }
      }

      await Promise.all(apiCalls)

      setStagedDepth(null)

      setStagedStation(null)

      setConfirmModalOpened(false)

      setIsUpdating(false)
    } catch (error) {
      setIsUpdating(false)

      showNotification({ type: 'error', message: translate.phrases.banyanApp('There was an error') })
    }
  }, [
    selectedStation,
    selectedDepth,
    pwp,
    fc,
    lastGoodReading,
    defaultLastGoodReading,
    setIsUpdating,
    setDefaultLastGoodReading,
    setLastGoodReading,
    setOverrides,
    setStagedDepth,
    setStagedStation,
    setConfirmModalOpened,
    getOverrideValues,
  ])

  useEffect(() => {
    if (!soilCalibrationSettingsOpened || !selectedPoint || selectedStation) return

    const initializeModal = async () => {
      setSelectedStation(selectedPoint)

      const stationPoints = allPropertiesPoints[selectedPoint]
      const depthsAvailable = stationPoints?.configuration?.soilProbeDepthsAvailable ?? []

      if (depthsAvailable.length > 0) {
        const firstDepth = removeCmOrInFromSoilProbeDepths(depthsAvailable[0])

        await fetchSoilData(selectedPoint, undefined, (freshOverrides) => {
          setupDepthAndOverrides(firstDepth, freshOverrides)
        })
      } else {
        await fetchSoilData(selectedPoint)
      }
    }

    initializeModal()
  }, [
    soilCalibrationSettingsOpened,
    selectedPoint,
    selectedStation,
    setSelectedStation,
    allPropertiesPoints,
    fetchSoilData,
    setupDepthAndOverrides,
  ])

  const onClose = useCallback(() => {
    if (somethingHasChanged) {
      setConfirmModalOpened(true)

      setStagedClose(true)

      return
    }

    const shouldRefresh = refreshChartAndSummary

    detailsPanelStore.setState((prev) => ({
      ...prev,
      soilCalibrationSettingsOpened: false,
      ...(shouldRefresh && { keyForRefreshing: new Date().toISOString() }),
    }))

    if (shouldRefresh) {
      setRefreshChartAndSummary(false)
    }

    refreshMapData(ValueGroupDictionary.soil)
  }, [refreshChartAndSummary, setRefreshChartAndSummary, somethingHasChanged])

  return (
    <ModalDrawer
      opened={soilCalibrationSettingsOpened}
      onClose={onClose}
      title={translate.phrases.banyanApp('Soil Calibration Settings')}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
      primaryButtonText={translate.phrases.banyanApp('Save')}
      primaryButtonOnPress={handleUpdate}
      secondaryButtonText={translate.phrases.banyanApp('Cancel')}
      secondaryButtonOnPress={onClose}
      primaryButtonDisabled={isUpdating}
      trapFocus={false} // trapFocus is false because we don't want to auto open the StationSelect
    >
      <LoadingOverlay visible={loading || isUpdating || isDeleting} />
      <SoilCalibrationModalContents
        data={selectedStation ? (data[selectedStation]?.values?.soilVwc ?? []) : []}
        dates={dates}
        fc={fc}
        isDeleting={isDeleting}
        lastGoodReading={lastGoodReading}
        overrides={overrides}
        pwp={pwp}
        selectedDepth={selectedDepth}
        selectedStation={selectedStation}
        setFc={setFc}
        setIsDeleting={setIsDeleting}
        setLastGoodReading={setLastGoodReading}
        setOverrides={setOverrides}
        setPwp={setPwp}
        handleStationChange={handleStationChange}
        handleDepthChange={handleDepthChange}
        handleDateChange={handleDateChange}
        soilProbeDepthsAvailable={soilProbeDepthsAvailable}
      />
      <ConfirmationSettingsModal
        confirmModalOpened={confirmModalOpened}
        setConfirmModalOpened={setConfirmModalOpened}
        handleResetAndClose={handleResetAndClose}
        handleUpdate={async () => {
          await handleUpdate()

          setConfirmModalOpened(false)
        }}
        zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 2}
      />
    </ModalDrawer>
  )
}
